import React, { createContext, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BrandConfig, BrandKey, getBrandConfig } from '../config/brands';
import {
  getBrandPath,
  getBrandRelativePathFromPathname,
  switchBrandInPathname,
} from '../utils/brandRouting';

interface BrandContextValue {
  brand: BrandConfig;
  brandKey: BrandKey;
  relativePath: string;
  getPath: (relativePath?: string) => string;
  switchBrand: (targetBrandKey: BrandKey) => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

interface BrandProviderProps {
  brandKey: BrandKey;
  children: React.ReactNode;
}

/**
 * Provides the active brand and route helpers to the app shell.
 * Why this matters: pages should ask for the current brand once instead of each
 * component re-parsing the URL and rebuilding paths independently.
 */
export const BrandProvider: React.FC<BrandProviderProps> = ({ brandKey, children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const contextValue = useMemo<BrandContextValue>(() => {
    const brand = getBrandConfig(brandKey);
    const relativePath = getBrandRelativePathFromPathname(location.pathname);

    return {
      brand,
      brandKey,
      relativePath,
      getPath: (nextRelativePath = '') => getBrandPath(brandKey, nextRelativePath),
      switchBrand: (targetBrandKey) => {
        navigate(switchBrandInPathname(location.pathname, targetBrandKey));
      },
    };
  }, [brandKey, location.pathname, navigate]);

  return <BrandContext.Provider value={contextValue}>{children}</BrandContext.Provider>;
};

/**
 * Read active brand information inside brand-prefixed routes.
 * Why this matters: this keeps brand-aware components simple and avoids prop drilling.
 */
export const useBrand = (): BrandContextValue => {
  const context = useContext(BrandContext);

  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }

  return context;
};
